import type { GridApi, IRowNode } from 'ag-grid-community'
import { isRecord } from '@/utils/typeGuards'
import { resetSelection } from './grid.utils'
import { clearRemovedVirtualizedCellFocus } from './focusBeforeRowRemoval'

type NewRowFactoryParams = {
  insertAfterRowId?: string
}

const getNodeId = (node: IRowNode | null | undefined): string | undefined => {
  const data: unknown = node?.data
  if (!isRecord(data)) return undefined
  return typeof data.id === 'string' ? data.id : undefined
}

const getRowId = <Row>(row: Row | undefined): string | undefined => {
  if (!isRecord(row)) return undefined
  const id = row.id
  if (typeof id !== 'string' || id.length === 0) return undefined
  return id
}

const selectRowsWhenAvailable = <Row>(
  api: GridApi<Row>,
  rowIds: string[],
): void => {
  const uniqueRowIds = [...new Set(rowIds.filter((rowId) => rowId.length > 0))]
  if (uniqueRowIds.length === 0) return

  const idsToSelect = new Set(uniqueRowIds)

  const selectRows = (): boolean => {
    const nodesById = new Map<string, IRowNode<Row>>()

    api.forEachNode((node) => {
      const rowId = getRowId(node.data)
      if (typeof rowId === 'string' && idsToSelect.has(rowId)) {
        nodesById.set(rowId, node)
      }
    })

    if (nodesById.size !== idsToSelect.size) return false

    resetSelection(api)
    const orderedNodes = uniqueRowIds.flatMap((rowId) => {
      const rowNode = nodesById.get(rowId)
      return rowNode ? [rowNode] : []
    })

    orderedNodes.forEach((rowNode) => {
      rowNode.setSelected(true)
    })

    const firstNode = orderedNodes[0]
    if (firstNode) {
      api.ensureNodeVisible(firstNode)
    }

    return true
  }

  if (selectRows()) return

  let remainingModelUpdates = 30
  const onModelUpdated = () => {
    remainingModelUpdates -= 1
    if (selectRows() || remainingModelUpdates <= 0) {
      api.removeEventListener('modelUpdated', onModelUpdated)
    }
  }

  api.addEventListener('modelUpdated', onModelUpdated)
}

/**
 * Adds a brand-new draft row at the focused-cell position (or top if nothing
 * is focused). The `factory` receives `insertAfterRowId` so the row can be
 * anchored by `postSortRowsPinNewRows`.
 */
export const addNewRowAtIndex = <Row>(
  api: GridApi<Row>,
  factory: (params: NewRowFactoryParams) => Partial<Row>,
): void => {
  const selectedCell = api.getFocusedCell()
  const rowIndex = (selectedCell?.rowIndex ?? 0) + 1
  const insertAfterNode = api.getDisplayedRowAtIndex(rowIndex - 1)
  const insertAfterRowId = getNodeId(insertAfterNode)

  resetSelection(api)
  const row = factory({ insertAfterRowId })
  api.applyTransaction({ add: [row as Row], addIndex: rowIndex })
}

/**
 * Duplicates every selected row using `factory` to produce the copy, then
 * selects the newly created rows. Pass `onCreated` to persist the copies
 * server-side.
 */
export const duplicateRows = <Row>(
  api: GridApi<Row>,
  factory: (source: Row) => Row,
  options?: {
    onCreated?: (rows: Row[]) => void
    sourceRows?: Row[]
  },
): void => {
  const sourceRows = options?.sourceRows ?? api.getSelectedRows()
  if (sourceRows.length === 0) return

  const copies = sourceRows.map(factory)
  api.applyTransaction({ add: copies })
  options?.onCreated?.(copies)
  selectRowsWhenAvailable(
    api,
    copies
      .map((row) => getRowId(row))
      .filter((id): id is string => Boolean(id)),
  )
}

/**
 * Replaces a placeholder row with a persisted row immediately in the grid, then
 * optionally persists the creation server-side via `onCreated`.
 */
export const persistNewRow = <Row>(
  api: GridApi<Row>,
  placeholderRow: Row,
  newRow: Row,
  options?: { onCreated?: (row: Row) => void },
): void => {
  api.applyTransaction({ remove: [placeholderRow], add: [newRow] })
  options?.onCreated?.(newRow)
}

/**
 * Removes rows immediately from the grid, then optionally persists the
 * deletion server-side via `onDeleted`.
 */
export const deleteRows = <Row>(
  api: GridApi<Row>,
  rows: Row[],
  options?: { onDeleted?: (rows: Row[]) => void },
): void => {
  if (rows.length === 0) return

  clearRemovedVirtualizedCellFocus(api, rows, (row) => getRowId(row) ?? null)
  api.applyTransaction({ remove: rows })
  options?.onDeleted?.(rows)
}
